import styled from '@emotion/styled'
import { FooterLinks } from '../components/form'
import React, { useRef } from 'react'
import { Helmet } from 'react-helmet'
import { color, screen, size } from '../styles'
import { useMotionValueEvent, useScroll, useTransform } from 'framer-motion'
import { maxWidth, padding } from '../styles/size'

interface Props {
   htmlTitle?: string | undefined
   illustrationSection: React.ReactNode
   mainSection: React.ReactNode
}

const ILLUSTRATION_HEIGHT = 180

const MultiSectionsLayout: React.FC<Props> = (props) => {
   const { illustrationSection, mainSection, htmlTitle } = props
   const illustrationContainerRef = useRef<HTMLElement>(null)
   const { scrollY } = useScroll()
   const mobileIllustrationHeight = useTransform(scrollY, [0, ILLUSTRATION_HEIGHT], [0, 180])

   useMotionValueEvent(mobileIllustrationHeight, 'change', (height) => {
      if (height > ILLUSTRATION_HEIGHT) return // do nothing if height is greater than 300
      const illustrationContainer = illustrationContainerRef.current
      const progress = height / ILLUSTRATION_HEIGHT
      const heightDiff = ILLUSTRATION_HEIGHT - height
      const mobileIllustrationContainer = illustrationContainer?.querySelector(
         '.tablet-and-mobile'
      ) as HTMLElement
      mobileIllustrationContainer.style.height = `max(80px, ${heightDiff}px)`
      mobileIllustrationContainer.style.setProperty('--animation-factor', progress + '')
   })

   return (
      <Holder>
         <Helmet>
            <title>{htmlTitle ?? 'RentPost'}</title>
         </Helmet>
         <section
            className="illustration-section"
            data-testid="illustration"
            ref={illustrationContainerRef}
         >
            {illustrationSection}
         </section>
         <section className="main-section" data-testid="main-section">
            <div className="children-holder">{mainSection}</div>
            <footer data-testid="footer">
               <span className="copy-rights">
                  Copyright © {new Date().getFullYear()} RentPost, Inc. All rights reserved.
               </span>
               <FooterLinks />
            </footer>
         </section>
      </Holder>
   )
}

const Holder = styled.div`
   display: flex;
   overflow: hidden;

   .illustration-section {
      position: relative;
      display: flex;
      z-index: 2;
      max-width: ${maxWidth.illustrationSection};
      overflow: hidden;
      flex: 1;
   }

   .main-section {
      position: relative;
      z-index: 1;
      position: relative;
      display: flex;
      flex-direction: column;
      flex-direction: column;
      height: 100vh;
      width: 100%;
      overflow-x: hidden;
      overflow-y: auto;
      scrollbar-gutter: stable;
      flex: 3;

      .children-holder {
         flex: 1;
         display: flex;
         flex-direction: column;
         justify-content: center;
         align-items: center;
         width: 100%;
         box-sizing: border-box;

         & > * {
            width: 100%;
            padding: ${padding.block} ${padding.inline};
            max-width: ${maxWidth.mainSection};
            box-sizing: border-box;
         }
      }
      footer {
         display: flex;
         justify-self: center;
         align-self: center;
         justify-content: space-around;
         align-items: center;
         flex-wrap: wrap;
         color: ${color.grayDark};
         width: 100%;
         padding: 1.5em ${padding.inline};
         gap: 0.6em;
         border-top: 1px solid #eee;
         user-select: none;

         span {
            font-size: ${size.xs};
         }
      }
   }

   /* RESPONSIVENESS */
   ${screen.tabletAndMobile} {
      --illustration-height: ${ILLUSTRATION_HEIGHT}px;
      flex-direction: column;

      .illustration-section {
         .tablet-and-mobile {
            --animation-factor: 0;
            position: fixed;
            height: ${ILLUSTRATION_HEIGHT}px;
            overflow: hidden;
            background-color: rgba(255, 255, 255, calc(var(--animation-factor) * 0.5));
            backdrop-filter: blur(calc(var(--animation-factor) * 5px));
            cursor: pointer;
            height: 180px;
            width: 100%;
            box-shadow: 0 0 0.5em rgba(0, 0, 0, 0.1);

            * {
               pointer-events: none;
            }

            /* multi logo (coloured & white) */
            .logo {
               position: absolute;
               top: 50%;
               left: 50%;
               width: 10em;
               transform: translate(-50%, -50%);
               z-index: 6;
               display: flex;
               justify-content: center;
               align-items: center;
               svg {
                  position: absolute;
                  width: 100%;
                  &.white {
                     fill: ${color.white};
                     opacity: calc(1 - var(--animation-factor));
                     stroke: ${color.white};
                     stroke-width: 1px;
                  }
                  &.colored {
                     opacity: var(--animation-factor);
                  }
               }
            }

            /* Image holder */
            .image-holder {
               position: absolute;
               z-index: 0;
               top: 0;
               left: 0;
               width: 100%;
               height: 100%;
               opacity: calc(1 - var(--animation-factor) + 0);
               overflow: hidden;
            }

            /* Darken overlay */
            &:after {
               content: '';
               position: absolute;
               top: 0;
               left: 0;
               width: 100%;
               height: 100%;
               /* create gradient background from white to transparent  */
               background: linear-gradient(
                  0deg,
                  rgba(255, 255, 255, 0) 0%,
                  rgba(255, 255, 255, 1) 100%
               );
               z-index: 2;
               backdrop-filter: blur(7px);
               opacity: var(--animation-factor);
            }

            &:before {
               content: '';
               position: absolute;
               top: 0;
               left: 0;
               width: 100%;
               height: 100%;
               /* create gradient background from white to transparent  */
               z-index: 2;
               background: rgba(0, 0, 0, max(0, calc(0.5 - var(--animation-factor))));
            }
         }
      }

      .main-section {
         margin-top: var(--illustration-height);
         overflow: hidden;
         height: 100%;
         min-height: calc(100dvh - var(--illustration-height));
         padding: 0;
      }
   }
`

export default MultiSectionsLayout
